import { useState, useEffect, useRef } from "react";
import { QrButtons } from "@/components/admin/QrTools";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import Pager from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, QrCode, CheckCircle2, AlertTriangle, CalendarCheck } from "lucide-react";
import { initials, PageHeader, InfoNote } from "@/components/common";

const STATUSES = [
  { v: "-", label: "–", cls: "bg-slate-400 text-white" },
  { v: "hadir", label: "Hadir", cls: "bg-emerald-600 text-white" },
  { v: "izin", label: "Izin", cls: "bg-amber-500 text-white" },
  { v: "sakit", label: "Sakit", cls: "bg-blue-500 text-white" },
  { v: "alpa", label: "Alpa", cls: "bg-rose-500 text-white" },
];
const KATEGORI = [
  { v: "ngaji_dhuha", label: "Keaktifan Ngaji Dhuha", needNama: false },
  { v: "keaktifan_departemen", label: "Keaktifan Departemen", needNama: false },
  { v: "keaktifan_ribath", label: "Keaktifan Ribath", needNama: false },
];
const MONTHS_DEP = [
  { v: "2026-07", label: "Juli 2026" }, { v: "2026-08", label: "Agustus 2026" },
  { v: "2026-09", label: "September 2026" }, { v: "2026-10", label: "Oktober 2026" },
  { v: "2026-11", label: "November 2026" }, { v: "2026-12", label: "Desember 2026" },
  { v: "2027-01", label: "Januari 2027" }, { v: "2027-02", label: "Februari 2027" },
  { v: "2027-03", label: "Maret 2027" }, { v: "2027-04", label: "April 2027" },
  { v: "2027-05", label: "Mei 2027" }, { v: "2027-06", label: "Juni 2027" },
];
const curMonth = () => { const d = new Date(); const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; return MONTHS_DEP.some((m) => m.v === ym) ? ym : "2026-08"; };
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const shiftDate = (d, n) => { const x = new Date(d + "T12:00:00"); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };

export default function AbsensiInput({ lockKategori, autoScan, onAutoScanDone, onScanStateChange }) {
  const qc = useQueryClient();
  const { data: santri = [] } = useQuery({ queryKey: ["santri"], queryFn: async () => (await api.get("/santri")).data });
  const { data: options = {} } = useQuery({ queryKey: ["options"], queryFn: async () => (await api.get("/options")).data });

  const cats = lockKategori ? KATEGORI.filter((k) => k.v === lockKategori) : KATEGORI.filter((k) => k.v !== "ngaji_dhuha");
  const [kategori, setKategori] = useState(lockKategori || "keaktifan_ribath");
  const [namaKegiatan, setNamaKegiatan] = useState("");
  const [tanggal, setTanggal] = useState(today());
  const [statuses, setStatuses] = useState({});
  const [q, setQ] = useState("");
  const [dep, setDep] = useState("all");
  const [rib, setRib] = useState("all");
  const [scanOpen, setScanOpen] = useState(false);
  const [bulan, setBulan] = useState(curMonth());
  const [persen, setPersen] = useState({});
  const [page, setPage] = useState(1);
  const [syncInfo, setSyncInfo] = useState(null);
  const PER_PAGE = 50;
  const lastScan = useRef({ t: "", at: 0 });
  useEffect(() => { if (autoScan) { setScanOpen(true); onAutoScanDone?.(); } }, [autoScan]);
  useEffect(() => { onScanStateChange?.(scanOpen); return () => onScanStateChange?.(false); }, [scanOpen]);

  const scanSaveMut = useMutation({
    mutationFn: async ({ santri_id }) => api.post("/absensi", {
      santri_id, tanggal, kategori, nama_kegiatan: needNama ? namaKegiatan : "", status: "hadir",
    }),
    onSuccess: (_r, vars) => {
      ["activities", "stats", "summary", "overview", "evaluasi", "evaluasi-rekap", "santri"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(`${vars.nama} → Hadir (tersimpan)`);
    },
    onError: (e, vars) => {
      setStatuses((p) => ({ ...p, [vars.santri_id]: "-" }));
      toast.error(`Gagal simpan ${vars.nama}: ${e?.response?.data?.detail || "coba lagi"}`);
    },
  });

  useEffect(() => {
    setStatuses((prev) => {
      const next = { ...prev };
      let changed = false;
      santri.forEach((s) => { if (!next[s.id]) { next[s.id] = "-"; changed = true; } });
      return changed ? next : prev;
    });
  }, [santri.length]);

  const handleScan = (text) => {
    const t = String(text).trim().toUpperCase();
    if (!t) return null;
    const now = Date.now();
    if (t === lastScan.current.t && now - lastScan.current.at < 2500) return { ok: false, dup: true };
    lastScan.current = { t, at: now };
    const found = santri.find((s) => {
      const nidi = String(s.nidi || "").toUpperCase();
      const ni = String(s.no_induk_pondok || "").toUpperCase();
      const nm = String(s.nama || "").toUpperCase();
      return (nidi && (t === nidi || t.includes(nidi))) || (ni && (t === ni || t.includes(ni))) || (nm && (t === nm || t.includes(nm)));
    });
    if (found) {
      setStatuses((p) => ({ ...p, [found.id]: "hadir" }));
      scanSaveMut.mutate({ santri_id: found.id, nama: found.nama });
      return { ok: true, nama: found.nama };
    }
    toast.error("Santri tidak ditemukan dari QR");
    return { ok: false, notfound: true };
  };

  const needNama = KATEGORI.find((k) => k.v === kategori)?.needNama;
  const filteredSantri = santri.filter((s) => (dep === "all" || s.departemen === dep) && (rib === "all" || s.ribath === rib) &&
    (s.nama.toLowerCase().includes(q.toLowerCase()) || (s.no_induk_pondok || "").includes(q)));
  const pages = Math.max(1, Math.ceil(filteredSantri.length / PER_PAGE));
  const safePage = Math.min(page, pages);
  const pageSantri = filteredSantri.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  const bulkMut = useMutation({
    mutationFn: async () => api.post("/absensi/bulk", {
      tanggal, kategori, nama_kegiatan: needNama ? namaKegiatan : "",
      entries: filteredSantri.map((s) => ({ santri_id: s.id, status: (!statuses[s.id] || statuses[s.id] === "-") ? "alpa" : statuses[s.id] })),
    }),
    onSuccess: (r) => {
      ["activities", "stats", "summary", "overview", "evaluasi", "evaluasi-rekap", "santri"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setStatuses((prev) => {
        const next = { ...prev };
        filteredSantri.forEach((s) => { if (!next[s.id] || next[s.id] === "-") next[s.id] = "alpa"; });
        return next;
      });
      const ss = r.data.sheet_sync; setSyncInfo(ss || null);
      toast.success(`Absensi tersimpan untuk ${r.data.created} santri`);
      if (ss) {
        if (ss.ok) toast.success(`Tersinkron ke Google Sheet (${ss.written} sel diperbarui)`);
        else if (ss.reason === "protected") toast.warning("Gagal sinkron ke Sheet: tab masih dilindungi. Data tetap aman di aplikasi.");
        else if (ss.reason === "permission") toast.warning("Gagal sinkron ke Sheet: akun tak punya izin edit. Data tetap aman di aplikasi.");
        else if (ss.reason === "token_expired") toast.warning("Koneksi Google kedaluwarsa — hubungkan ulang di menu Pengaturan. Data tetap aman di aplikasi.");
        else if (ss.reason !== "not_configured" && ss.reason !== "skipped") toast.error("Gagal sinkron ke Sheet. Data tetap aman di aplikasi.");
      }
      setNamaKegiatan("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Gagal menyimpan"),
  });

  const setAll = (v) => { setStatuses((prev) => { const n = { ...prev }; santri.forEach((s) => (n[s.id] = v)); return n; }); };

  const depMode = kategori === "keaktifan_departemen" || kategori === "keaktifan_ribath";
  const depMut = useMutation({
    mutationFn: async () => api.post("/absensi/keaktifan-persen", {
      kategori, bulan,
      entries: filteredSantri
        .filter((s) => persen[s.id] !== undefined && persen[s.id] !== "" && !isNaN(Number(persen[s.id])))
        .map((s) => ({ santri_id: s.id, persen: Number(persen[s.id]) })),
    }),
    onSuccess: (r) => {
      ["santri", "summary", "stats", "overview", "evaluasi", "evaluasi-rekap"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      const ss = r.data.sheet_sync; setSyncInfo(ss || null);
      toast.success(`${cats[0]?.label || "Keaktifan"} tersimpan untuk ${r.data.saved} santri`);
      if (ss) {
        if (ss.ok) toast.success(`Tersinkron ke Google Sheet (${ss.written} santri diperbarui)`);
        else if (ss.reason === "protected") toast.warning("Gagal sinkron ke Sheet: tab masih dilindungi. Data tetap aman di aplikasi.");
        else if (ss.reason === "permission") toast.warning("Gagal sinkron: akun tak punya izin edit. Data tetap aman di aplikasi.");
        else if (ss.reason === "token_expired") toast.warning("Koneksi Google kedaluwarsa — hubungkan ulang di menu Pengaturan. Data tetap aman di aplikasi.");
        else if (ss.reason !== "not_configured" && ss.reason !== "skipped") toast.error("Gagal sinkron ke Sheet. Data tetap aman di aplikasi.");
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Gagal menyimpan"),
  });
  const depCount = filteredSantri.filter((s) => persen[s.id] !== undefined && persen[s.id] !== "").length;

  return (
    <div className="space-y-5 pb-24 sm:pb-0">
      <PageHeader icon={CalendarCheck} title={lockKategori ? `Input Absensi ${cats[0]?.label || ""}` : "Input Absensi"} subtitle={depMode ? "Isi persentase keaktifan bulanan tiap santri." : "Catat kehadiran Ngaji Dhuha."} />

      <div className={`glass rounded-2xl p-5 grid gap-4 ${lockKategori ? "" : "sm:grid-cols-2"}`}>
        {!lockKategori && (
          <div className="space-y-1.5">
            <Label className="text-xs">Kategori</Label>
            <Select value={kategori} onValueChange={setKategori}>
              <SelectTrigger data-testid="absensi-kategori-select"><SelectValue /></SelectTrigger>
              <SelectContent>{cats.map((k) => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs">{depMode ? "Bulan" : "Tanggal"}</Label>
          {depMode ? (
            <Select value={bulan} onValueChange={setBulan}>
              <SelectTrigger data-testid="departemen-bulan-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS_DEP.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <div className="flex items-center gap-1">
              <Button type="button" variant="outline" size="icon" className="shrink-0 rounded-xl" data-testid="date-prev-button" onClick={() => setTanggal(shiftDate(tanggal, -1))}><ChevronLeft className="h-4 w-4" /></Button>
              <Input type="date" value={tanggal} data-testid="absensi-tanggal-input" onChange={(e) => setTanggal(e.target.value)} className="text-center" />
              <Button type="button" variant="outline" size="icon" className="shrink-0 rounded-xl" data-testid="date-next-button" onClick={() => setTanggal(shiftDate(tanggal, 1))}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
      </div>

      {depMode ? (
        <InfoNote label="Cara isi Keaktifan (persentase)">
          Isi <strong>persentase keaktifan</strong> (0–100) tiap santri untuk bulan <strong>{MONTHS_DEP.find((m) => m.v === bulan)?.label}</strong>. Saat disimpan, kolom <strong>centang harian</strong> bulan itu di Google Sheet otomatis terisi sesuai persentase (jumlah/total) — rumus % tetap dipakai, tidak ditimpa.
        </InfoNote>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500 dark:text-slate-300">Tandai semua:</span>
            {STATUSES.map((st) => <Button key={st.v} size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => setAll(st.v)} data-testid={`mark-all-${st.v === "-" ? "reset" : st.v}`}>{st.label}</Button>)}
            <Button size="sm" className="rounded-full h-7 text-xs gap-1 bg-emerald-700 hover:bg-emerald-800 text-white ml-auto" data-testid="scan-qr-button" onClick={() => setScanOpen(true)}><QrCode className="h-3.5 w-3.5" /> Scan QR</Button>
          </div>
          <InfoNote label="Cara kerja absensi & Scan QR">
            Default semua "–". Saat disimpan, santri yang masih "–" otomatis jadi <strong>Alpa</strong>. Hasil <strong>Scan QR</strong> langsung tersimpan ke web &amp; Google Sheet sebagai <strong>Hadir</strong> (tanpa perlu klik Simpan).
          </InfoNote>
        </>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:items-center gap-2">
        <Input data-testid="absensi-search-input" placeholder="Cari nama / no induk..." value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="w-full lg:w-56 rounded-xl bg-white/60 dark:bg-white/5 h-9" />
        <Select value={dep} onValueChange={setDep}>
          <SelectTrigger className="w-full lg:w-44 rounded-xl bg-white/60 dark:bg-white/5 h-9" data-testid="absensi-departemen-filter"><SelectValue placeholder="Semua Departemen" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Departemen</SelectItem>{(options.departemen || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={rib} onValueChange={setRib}>
          <SelectTrigger className="w-full lg:w-44 rounded-xl bg-white/60 dark:bg-white/5 h-9" data-testid="absensi-ribath-filter"><SelectValue placeholder="Semua Ribath" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Ribath</SelectItem>{(options.ribath || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-xs text-slate-400 dark:text-slate-500 dark:text-slate-300 sm:col-span-2 lg:col-span-1 lg:ml-1">{filteredSantri.length} santri</span>
      </div>
      <Pager page={safePage} pages={pages} total={filteredSantri.length} onChange={setPage} testId="absensi-pager" />

      <div className="grid gap-2">
        {pageSantri.map((s) => (
          <div key={s.id} className="glass rounded-xl p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 overflow-hidden" data-testid={`absensi-row-${s.no_induk_pondok}`}>
            <div className="flex items-center gap-3 min-w-0 w-full sm:w-auto">
              <Avatar className="h-9 w-9"><AvatarImage src={s.foto} /><AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs">{initials(s.nama)}</AvatarFallback></Avatar>
              <div className="min-w-0"><p className="font-medium text-emerald-950 dark:text-emerald-50 truncate">{s.nama}</p><p className="text-xs text-slate-400 dark:text-slate-500 dark:text-slate-300 truncate">{s.no_induk_pondok} · {s.departemen} · {s.ribath}</p></div>
            </div>
            <div className="flex gap-1.5 flex-wrap shrink-0 w-full sm:w-auto max-w-full min-w-0">
              {depMode ? (
                <div className="flex items-center gap-1.5 w-full sm:w-auto">
                  <Input type="number" min={0} max={100} inputMode="numeric" placeholder="%" value={persen[s.id] ?? ""}
                    data-testid={`departemen-persen-${s.no_induk_pondok}`}
                    onChange={(e) => setPersen((p) => ({ ...p, [s.id]: e.target.value }))}
                    className="h-9 w-24 rounded-lg text-center bg-white/70 dark:bg-white/5" />
                  <span className="text-sm text-slate-400 dark:text-slate-500 dark:text-slate-300">%</span>
                </div>
              ) : (
                STATUSES.map((st) => (
                  <button key={st.v} data-testid={`status-${s.no_induk_pondok}-${st.v === "-" ? "reset" : st.v}`} onClick={() => setStatuses((prev) => ({ ...prev, [s.id]: st.v }))}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${statuses[s.id] === st.v ? st.cls : "bg-slate-100 text-slate-500 dark:text-slate-300 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-300 dark:hover:bg-white/20"}`}>{st.label}</button>
                ))
              )}
            </div>
          </div>
        ))}
        {santri.length === 0 && <div className="glass rounded-2xl p-8 text-center text-slate-400 dark:text-slate-500 dark:text-slate-300">Belum ada santri.</div>}
      </div>

      {depMode ? (
        <Button data-testid="admin-submit-departemen-button" onClick={() => depMut.mutate()}
          disabled={depMut.isPending || depCount === 0}
          className="w-full h-12 rounded-xl bg-emerald-700 hover:bg-emerald-800">Simpan {cats[0]?.label || "Keaktifan"} ({depCount} terisi)</Button>
      ) : (
        <Button data-testid="admin-submit-absensi-button" onClick={() => bulkMut.mutate()}
          disabled={bulkMut.isPending || filteredSantri.length === 0}
          className="w-full h-12 rounded-xl bg-emerald-700 hover:bg-emerald-800">Simpan Absensi ({filteredSantri.length} santri)</Button>
      )}

      {syncInfo && (
        <div data-testid="sheet-sync-status" className={`text-xs rounded-xl px-3 py-2.5 flex items-center gap-2 ${syncInfo.ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : (syncInfo.reason === "not_configured" || syncInfo.reason === "skipped") ? "bg-slate-50 text-slate-500 dark:text-slate-300 border border-slate-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`}>
          {syncInfo.ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
          <span>{syncInfo.ok ? `Tersinkron ke Google Sheet ✓ — ${syncInfo.written} sel diperbarui` : syncInfo.reason === "protected" ? "Gagal sinkron ke Google Sheet: tab masih dilindungi. Data tetap tersimpan di aplikasi." : syncInfo.reason === "permission" ? "Gagal sinkron: akun Google tak punya izin edit. Data tetap tersimpan di aplikasi." : syncInfo.reason === "token_expired" ? "Koneksi Google kedaluwarsa — hubungkan ulang akun di menu Pengaturan. Data tetap tersimpan di aplikasi." : syncInfo.reason === "not_configured" ? "Google Sheet belum terhubung — data hanya tersimpan di aplikasi." : "Gagal sinkron ke Google Sheet. Data tetap tersimpan di aplikasi."}</span>
        </div>
      )}

      <QRScanDialog open={scanOpen} onClose={() => setScanOpen(false)} onScan={handleScan} />
    </div>
  );
}

function QRScanDialog({ open, onClose, onScan }) {
  const [err, setErr] = useState("");
  const [last, setLast] = useState(null);
  const [count, setCount] = useState(0);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const audioRef = useRef(null);
  const beep = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = audioRef.current || (audioRef.current = new AC());
      if (ctx.state === "suspended") ctx.resume();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = 880;
      o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
      o.start(); o.stop(ctx.currentTime + 0.2);
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    if (!open) return;
    setErr(""); setLast(null); setCount(0);
    let scanner;
    let cancelled = false;
    const cfg = {
      fps: 10,
      qrbox: (vw, vh) => { const m = Math.floor(Math.min(vw, vh) * 0.7); return { width: m, height: m }; },
      aspectRatio: 1.0,
    };
    // Callback tetap memindai terus (kamera tidak ditutup setelah 1 scan)
    const onDecode = (text) => {
      const res = onScanRef.current && onScanRef.current(text);
      if (res && res.ok) {
        setLast({ nama: res.nama, at: Date.now() });
        setCount((c) => c + 1);
        beep();
        try { if (navigator.vibrate) navigator.vibrate(120); } catch (e) { /* noop */ }
      }
    };
    import("html5-qrcode").then(async ({ Html5Qrcode }) => {
      if (cancelled) return;
      scanner = new Html5Qrcode("qr-reader");
      const startWith = async (camera) => scanner.start(camera, cfg, onDecode, () => {});
      try {
        const cams = await Html5Qrcode.getCameras();
        if (cancelled) return;
        if (cams && cams.length) {
          const back = cams.find((c) => /back|rear|environment/i.test(c.label));
          await startWith({ deviceId: { exact: (back || cams[cams.length - 1]).id } });
        } else {
          await startWith({ facingMode: "environment" });
        }
      } catch (e1) {
        try { await startWith({ facingMode: "user" }); }
        catch (e2) {
          const msg = (e2 && e2.name === "NotAllowedError") || /permission|denied/i.test(String(e2))
            ? "Akses kamera ditolak. Izinkan kamera untuk situs ini di pengaturan browser, lalu coba lagi."
            : "Tidak dapat mengakses kamera. Pastikan tidak ada aplikasi lain yang memakainya.";
          setErr(msg); toast.error(msg);
        }
      }
    }).catch(() => { setErr("Gagal memuat pemindai."); });
    return () => {
      cancelled = true;
      if (scanner) {
        try {
          if (scanner.isScanning) scanner.stop().then(() => scanner.clear()).catch(() => {});
          else scanner.clear();
        } catch (e) { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="qr-scan-dialog">
        <DialogHeader><DialogTitle>Scan QR Santri</DialogTitle></DialogHeader>
        <div id="qr-reader" style={{ width: "100%", borderRadius: 12, overflow: "hidden" }} />
        {last && (
          <div className="rounded-xl bg-emerald-600 text-white text-center py-2 px-3 text-sm font-semibold animate-in fade-in" data-testid="qr-scan-success">
            ✓ {last.nama} → Hadir (tersimpan) · Total ter-scan: {count}
          </div>
        )}
        {err && <p className="text-xs text-rose-600 text-center" data-testid="qr-scan-error">{err}</p>}
        <p className="text-xs text-slate-500 dark:text-slate-300 text-center">Arahkan kamera ke QR Code (berisi NIDI / Nama Santri). Kamera tetap terbuka — cukup arahkan ke QR berikutnya. Santri yang cocok <strong>otomatis ditandai Hadir & langsung tersimpan</strong> ke web dan Google Sheet.</p>
      </DialogContent>
    </Dialog>
  );
}
